<script setup>
import { formatDateTimeByRegion, randomColor } from '../../../helpers';
import { formatPrice } from '../../priceTargets/utils';
import { calculatePerformance, formatPerformance, getDirectionBackground } from './utils';

const props = defineProps({
    trades: {
        type: Array,
        required: true
    }
});
</script>

<template>
    <div class="space-y-3 font-sans">
        <div class="hidden lg:flex items-center px-4 text-xxs font-semibold text-gray-500">
            <div class="flex-1 basis-3/12">{{ $t('plain_values') }}</div>
            <div class="flex-1 basis-1/12 text-right">{{ $t('plain_volume') }}</div>
            <div class="flex-1 basis-2/12 text-right">{{ $t('plain_entry') }}</div>
            <div class="flex-1 basis-2/12 text-center">{{ $t('plain_price') }}</div>
            <div class="flex-1 basis-2/12 text-center">Performance</div>
            <div class="flex-1 basis-1/12 text-right">Take Profit</div>
            <div class="flex-1 basis-1/12 text-right">Stopp</div>
            <div class="flex-1 basis-2/12 text-right">{{ $t('plain_exit') }}</div>
            <div class="flex-1 basis-1/12 text-right">{{ $t('plain_analysis') }}</div>
        </div>
        <ul class="shadow-md rounded-2xl">
            <li
                v-for="(trade, index) in props.trades"
                :key="trade.id"
                :class="[index % 2 === 0 ? 'bg-dashboard-primary' : '']"
                class="flex flex-col lg:flex-row lg:items-center gap-y-3 p-3 my-2 md:my-0 bg-white rounded-2xl transition-shadow duration-200"
            >
                <div class="lg:basis-3/12 flex items-center gap-3">
                    <div class="relative flex-shrink-0">
                        <img
                            v-if="trade.market_symbol.logo"
                            :src="trade.market_symbol.logo"
                            :alt="trade.market_symbol.name"
                            class="w-10 h-10 object-contain rounded-full bg-gray-100 p-1"
                            :class="[trade.exit_price ? 'opacity-50' : '']"
                        />
                        <div v-else :style="{ backgroundColor: randomColor() }" class="flex items-center justify-center rounded-full w-10 h-10">
                            <i class="fi fi-rr-apartment text-white text-lg"></i>
                        </div>
                        <div
                            v-if="trade.exit_price"
                            class="absolute bottom-0 right-0 bg-gray-600/80 rounded-full w-5 h-5 flex items-center justify-center border-2 border-white"
                            :title="$t('trade_closed')"
                        >
                            <i class="fi fi-rr-lock text-white text-[8px]"></i>
                        </div>
                    </div>
                    <span class="font-semibold text-xs truncate">{{ trade.market_symbol.name }}</span>
                </div>
                <div class="lg:basis-1/12 flex items-center justify-between lg:justify-end">
                    <span class="text-gray-500 lg:hidden text-xs">{{ $t('plain_volume') }}:</span>
                    <p class="font-semibold text-xs">{{ trade.amount }}</p>
                </div>
                <div class="lg:basis-2/12 flex flex-col">
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-gray-500 lg:hidden text-xs">{{ $t('plain_entry') }}:</span>

                        <span class="font-semibold px-2 py-0.5 rounded-full text-xs ml-5" :class="getDirectionBackground(trade.direction)">
                            {{ trade.direction }}
                        </span>

                        <p class="text-xs ml-auto text-right">{{ trade?.market_symbol?.currency }} {{ formatPrice(trade.entry_price) || '-' }}</p>
                    </div>

                    <div class="w-full flex justify-end">
                        <span class="text-xxs text-gray-400 flex items-center gap-1">
                            <i class="fi fi-rr-clock font-bold"></i>
                            {{ formatDateTimeByRegion(trade.entry_time) }}
                        </span>
                    </div>
                </div>

                <div class="lg:basis-2/12 flex items-center justify-between lg:justify-center">
                    <span class="text-gray-500 lg:hidden text-xs">{{ $t('plain_price') }}:</span>
                    <div class="text-center">
                        <p class="text-xs">{{ trade?.market_symbol?.currency }} {{ formatPrice(trade.market_symbol.last_price) }}</p>
                    </div>
                </div>
                <div class="lg:basis-2/12 flex items-center justify-between lg:justify-center">
                    <span class="text-gray-500 lg:hidden text-xs">Performance:</span>
                    <span
                        class="inline-block px-2 py-0.5 rounded-full text-xs"
                        :class="{
                            'bg-green/10 text-green ': calculatePerformance(trade) > 0,
                            'bg-red/10 text-red': calculatePerformance(trade) < 0,
                            'bg-gray-100 text-gray-600': calculatePerformance(trade) === 0 || calculatePerformance(trade) === null
                        }"
                    >
                        {{ formatPerformance(calculatePerformance(trade)) }}
                    </span>
                </div>
                <div class="lg:basis-1/12 flex items-center justify-between lg:justify-end">
                    <span class="text-gray-500 lg:hidden text-xs">Profit:</span>
                    <span v-if="trade.take_profit" class="text-xs"> {{ trade?.market_symbol?.currency }} {{ formatPrice(trade.take_profit) }} </span>
                    <span v-else class="text-xs">-</span>
                </div>
                <div class="lg:basis-1/12 flex items-center justify-between lg:justify-end">
                    <span class="text-gray-500 lg:hidden text-xs">{{ $t('plain_stop') }}:</span>
                    <span v-if="trade.stop_loss" class="text-xs"> {{ trade?.market_symbol?.currency }} {{ formatPrice(trade.stop_loss) }} </span>
                    <span v-else class="text-xs">-</span>
                </div>
                <div class="lg:basis-2/12 flex items-center justify-between lg:justify-end">
                    <span class="text-gray-500 lg:hidden text-xs">{{ $t('plain_exit') }}:</span>
                    <div class="text-right">
                        <p class="text-xs">{{ trade.exit_price ? `${trade?.market_symbol?.currency || ''} ${formatPrice(trade.exit_price)}` : '-' }}</p>
                        <span v-if="trade.exit_time" class="text-xxs text-gray-400 flex items-center justify-end gap-1">
                            <i class="fi fi-rr-clock font-bold"></i>
                            {{ formatDateTimeByRegion(trade.exit_time) }}
                        </span>
                        <span v-else class="text-xs text-gray-400">-</span>
                    </div>
                </div>
                <div class="lg:basis-1/12 flex justify-end">
                    <router-link v-if="trade.recent_contentstream" :to="`/dashboard/markets-analysis/${trade.recent_contentstream}`" class="cursor-pointer w-full lg:w-auto">
                        <div class="w-full px-2 py-1 rounded-lg font-semibold transition-colors duration-200 border hover:text-blue bg-blue-light text-blue/70 text-center">
                            <span class="lg:hidden">{{ $t('plain_analysis') }}</span>
                            <i class="fi fi-rs-arrow-right text-xs mt-1 hidden lg:inline-block"></i>
                        </div>
                    </router-link>
                    <div v-else class="w-full lg:w-auto">
                        <div class="w-full px-2 py-1 rounded-lg font-semibold border bg-gray-100 text-gray-400 cursor-not-allowed text-center">
                            <span class="lg:hidden">{{ $t('plain_analysis') }}</span>
                            <i class="fi fi-rs-arrow-right text-xs mt-1 hidden lg:inline-block"></i>
                        </div>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>
